import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogContent } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import API_BASE_URL from '../../../../Constants';
import DeviceAccordion from './components/DeviceAccordion';
import { RenderFields } from './components/RenderFields';

import {
  deviceGeneralInfo,
  deviceContractInfo,
  deviceOtherInfo,
  deviceClassToSubclassMap,
} from './config/deviceConfig';

/* -----------------------------------------------
   DeviceInspections Component (unchanged)
   Shows the device's existing inspections.
------------------------------------------------ */
const DeviceInspections = ({ inspections }) => {
  return (
    <DeviceAccordion label="Device Inspections">
      <div className="p-4">
        {inspections.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inspections.map((inspection) => (
              <div
                key={inspection.inspectionId}
                className="bg-white p-4 rounded-md border border-gray-300 shadow-sm"
              >
                <p className="font-medium">
                  Inspection ID: {inspection.inspectionId}
                </p>
                <p className="text-gray-600 text-sm">
                  Test: {inspection.test || 'N/A'}
                </p>
                <p className="text-gray-600 text-sm">
                  Approval Date: {inspection.approvalDate || 'N/A'}
                </p>
                <p className="text-gray-600 text-sm">
                  Task Type: {inspection.taskType || 'N/A'}
                </p>
                {/* Add more fields as needed */}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">No inspections found for this device.</p>
        )}
      </div>
    </DeviceAccordion>
  );
};

/* -----------------------------------------------
   Main ViewDeviceModal Component
   - Fetches device + inspections
   - Fetches agencies
   - Shows data in accordions
   - Allows editing/deleting device
------------------------------------------------ */
const ViewDeviceModal = ({
  open,
  id,
  refreshDevices,
  onClose,
  setNext,
  setPrev,
  buildings,
  customers,
  managementCompanies,
}) => {
  const [deviceData, setDeviceData] = useState({});
  const [inspectionData, setInspectionData] = useState([]);

  // Edit/expand states
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Subclass logic
  const [subclassOptions, setSubclassOptions] = useState([]);

  // For tracking selected building's customer/management
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedManagement, setSelectedManagement] = useState(null);

    

  /* --------------------------
     2) Sync selected customer/management
     whenever deviceData changes
     -------------------------- */
  useEffect(() => {
    if (deviceData.customerId && customers.length > 0) {
      const customer = customers.find(
        (c) => c.customerID === deviceData.customerId
      );
      setSelectedCustomer({
        customerId: customer?.customerID || null,
        customerName: customer?.customerName || 'N/A',
      });
    } else {
      setSelectedCustomer(null);
    }

    if (deviceData.managementId && managementCompanies.length > 0) {
      const management = managementCompanies.find(
        (m) => m.managementId === deviceData.managementId
      );
      setSelectedManagement({
        managementId: management?.managementId || null,
        managementName: management?.companyName || 'N/A',
      });
    } else {
      setSelectedManagement(null);
    }
  }, [
    deviceData.customerId,
    deviceData.managementId,
    customers,
    managementCompanies,
  ]);

  /* --------------------------
     3) Fetch device + inspections
     when ID & modal are open
     -------------------------- */
  useEffect(() => {
    const fetchDevice = async () => {
      try {
          const response = await fetch(
              `${API_BASE_URL}/api/setup/buildings/get-device/${id}`,
              {
                  method: 'GET',
                  headers: {
                      'Content-Type': 'application/json',
                  },
                  credentials: 'include', // Equivalent to withCredentials: true
              }
          );
  
          if (!response.ok) {
              throw new Error(`API responded with status ${response.status}`);
          }
  
          const data = await response.json();
          setDeviceData(data);
  
          const initialClass = data.deviceClass;
          if (initialClass) {
              setSubclassOptions(deviceClassToSubclassMap[initialClass] || []);
          }
  
      } catch (error) {
          console.error('Error fetching device:', error.message);
      }
  };
  

  const fetchInspections = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/inspections/device/${id}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );
  
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
  
      const data = await response.json();
      setInspectionData(data);
    } catch (error) {
      console.error('Error fetching inspections:', error);
    }
  };
  

    if (id && open) {
      fetchDevice();
      fetchInspections();
    }
  }, [id, open]);

  /* --------------------------
     4) Toggling edit & expand
     -------------------------- */
  const toggleEditMode = () => setIsEditMode(!isEditMode);
  const toggleExpand = () => setIsExpanded(!isExpanded);

  /* --------------------------
     5) Validation
     -------------------------- */
  const validateFields = () => {
    const errors = {};

    if (
      !deviceData.buildingId ||
      deviceData.buildingId === '' ||
      deviceData.buildingId === 0
    ) {
      errors.buildingId = 'Building is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* --------------------------
     6) Save changes
     -------------------------- */
  const handleSaveChanges = async () => {
    const isValid = validateFields();
    if (!isValid) {
        alert('Please fix the errors before saving.');
        return;
    }

    try {
        const response = await fetch(
            `${API_BASE_URL}/api/setup/buildings/devices/${id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(deviceData),
                credentials: 'include', // Equivalent to withCredentials: true
            }
        );

        if (!response.ok) {
            throw new Error(`API responded with status ${response.status}`);
        }

        alert('Device updated successfully');
        setIsEditMode(false);
        refreshDevices();
    } catch (error) {
        console.error('Error updating device:', error.message);
        alert('Failed to update device.');
    }
};

  
const handleDeleteDevice = async () => {
  if (window.confirm('Are you sure you want to delete this device?')) {
      try {
          const response = await fetch(
              `${API_BASE_URL}/api/setup/buildings/devices/${id}`,
              {
                  method: 'DELETE',
                  credentials: 'include', // Equivalent to withCredentials: true
              }
          );

          if (!response.ok) {
              throw new Error(`API responded with status ${response.status}`);
          }

          alert('Device deleted successfully');
          onClose();
          refreshDevices();
      } catch (error) {
          console.error('Error deleting device:', error.message);
          alert('Failed to delete device.');
      }
  }
};


  /* --------------------------
     8) Splitting fields for two-column layout
     -------------------------- */
  const splitFields = (fieldsArray) => {
    const midIndex = Math.ceil(fieldsArray.length / 2);
    return [fieldsArray.slice(0, midIndex), fieldsArray.slice(midIndex)];
  };

  /* --------------------------
     9) Field changes
     -------------------------- */
  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;

    setDeviceData((prev) => {
      const updatedData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // If buildingId changes, also update customerId & managementId
      if (name === 'buildingId') {
        const selectedBuilding = buildings.find(
          (b) => b.buildingID === value
        );
        if (selectedBuilding) {
          const associatedCustomer = customers.find(
            (c) => c.customerID === selectedBuilding.customerID
          );
          const associatedManagement = associatedCustomer
            ? managementCompanies.find(
                (m) => m.managementId === associatedCustomer.managementID
              )
            : null;

          updatedData.customerId = selectedBuilding.customerID || null;
          updatedData.managementId = associatedCustomer?.managementID || null;

          setSelectedCustomer(
            associatedCustomer
              ? {
                  customerId: associatedCustomer.customerID,
                  customerName: associatedCustomer.customerName,
                }
              : null
          );
          setSelectedManagement(
            associatedManagement
              ? {
                  managementId: associatedManagement.managementID,
                  managementName: associatedManagement.companyName,
                }
              : null
          );
        } else {
          updatedData.customerId = null;
          updatedData.managementId = null;
          setSelectedCustomer(null);
          setSelectedManagement(null);
        }
      }

      return updatedData;
    });
  };

  /* --------------------------
     10) Prev / Next device
     -------------------------- */
  const handlePrev = () => {
    setPrev();
    setIsEditMode(false);
  };

  const handleNext = () => {
    setNext();
    setIsEditMode(false);
  };

  /* --------------------------
     11) onClose w/ edit check
     -------------------------- */
  const handleClose = () => {
    if (!isEditMode) {
      setIsEditMode(false);
      onClose();
    }
  };

  const fetchInspections = async (deviceId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/inspections/device/${id}`,
        {
          method: 'GET',
          credentials: 'include', // Equivalent to withCredentials: true
        }
      );
  
      if (!response.ok) {
        throw new Error(`API responded with status ${response.status}`);
      }
  
      const data = await response.json();
      setInspectionData(data);
    } catch (error) {
      console.error('Error fetching inspection:', error.message);
    }
  };
  

  const handleClassChange = (e) => {
    const selectedClass = e.target.value;
    setDeviceData((prev) => ({
      ...prev,
      deviceClass: selectedClass,
      deviceSubclass: '',
    }));
    setSubclassOptions(deviceClassToSubclassMap[selectedClass] || []);
  };

  /* --------------------------------------------------
     Rendering the dialog with all your original logic
     -------------------------------------------------- */
  return (
    <>
      {/* Navigation Buttons */}
      {open && !isEditMode && (
        <>
          <button
            onClick={handlePrev}
            style={{
              position: 'fixed',
              left: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={handleNext}
            style={{
              position: 'fixed',
              right: `calc(50% - ${isExpanded ? '45%' : '500px'} - 60px)`,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1500,
            }}
            className="p-2 rounded-full bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </>
      )}

      {/* Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        PaperProps={{
          style: {
            width: isExpanded ? '90%' : '1000px',
            maxHeight: '90vh',
            minHeight: '90vh',
          },
        }}
        fullWidth
        style={{ zIndex: 1400 }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center p-4">
            <div className="flex items-center space-x-4 flex-grow">
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-gray-200 transition"
                aria-label="Back"
              >
                <ArrowBackIcon />
              </button>
              <h2 className="text-2xl font-bold text-left">
                {isEditMode
                  ? `Edit Device ID: ${id}`
                  : `View Device ID: ${id}`}
              </h2>
            </div>
            <button
              onClick={toggleExpand}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
            >
              {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </button>
          </div>

          <div className="flex space-x-4 p-4">
            {isEditMode ? (
              <>
                <button
                  onClick={toggleEditMode}
                  className="px-4 py-2 bg-sss-light-gray text-sss-blue-sel font-bold rounded-md hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveChanges}
                  className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
                >
                  Save
                </button>
                <button
                  onClick={handleDeleteDevice}
                  className="px-6 py-2 bg-red-500 text-white font-bold rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </>
            ) : (
              <button
                onClick={toggleEditMode}
                className="px-6 py-2 bg-sss-accent-yel text-sss-blue-sel font-bold rounded-md hover:bg-yellow-600 transition"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {/* Accordion Sections */}
        <DialogContent dividers>
          {/* General Info Accordion */}
          {(() => {
            const [generalInfoLeft, generalInfoRight] = splitFields(
              deviceGeneralInfo
            );
            return (
              <DeviceAccordion label="General Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={generalInfoLeft}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      handleClassChange={handleClassChange}
                      subclassOptions={subclassOptions}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      selectedCustomer={selectedCustomer}
                      selectedManagement={selectedManagement}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={generalInfoRight}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      handleClassChange={handleClassChange}
                      subclassOptions={subclassOptions}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      selectedCustomer={selectedCustomer}
                      selectedManagement={selectedManagement}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}

          {/* Contract Information Accordion */}
          {(() => {
            const [contractInfoLeft, contractInfoRight] = splitFields(
              deviceContractInfo
            );
            return (
              <DeviceAccordion label="Contract Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={contractInfoLeft}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={contractInfoRight}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}

          {/* Other Info Accordion */}
          {(() => {
            const [otherInfoLeft, otherInfoRight] = splitFields(
              deviceOtherInfo
            );
            return (
              <DeviceAccordion label="Other Information">
                <div
                  className={`flex ${
                    isExpanded ? 'flex-row' : 'flex-col md:flex-row'
                  } gap-4`}
                >
                  <div className="w-full">
                    <RenderFields
                      fields={otherInfoLeft}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                  <div className="w-full">
                    <RenderFields
                      fields={otherInfoRight}
                      formData={deviceData}
                      handleChange={isEditMode ? handleFieldChange : null}
                      readOnly={!isEditMode}
                      buildings={buildings}
                      validationErrors={validationErrors}
                    />
                  </div>
                </div>
              </DeviceAccordion>
            );
          })()}

          {/* Existing Inspections for this Device */}
          <DeviceInspections inspections={inspectionData} />

  
        </DialogContent>
      </Dialog>
    </>
  );
};

ViewDeviceModal.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.string,
  refreshDevices: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  setNext: PropTypes.func.isRequired,
  setPrev: PropTypes.func.isRequired,
  buildings: PropTypes.array.isRequired,
  customers: PropTypes.array.isRequired,
  managementCompanies: PropTypes.array.isRequired,
};

export default ViewDeviceModal;
